import React, { useRef, useEffect, useState } from "react";
import YouTubePlayer from "./YouTubePlayer";
import DirectVideoPlayer from "./DirectVideoPlayer";
import HlsVideoPlayer from "./HlsVideoPlayer";
import EmbedVideoPlayer from "./EmbedVideoPlayer";
import { assetsService } from "../../services/assetsService"; // Add this import
import {
  getYouTubeVideoId,
  isYouTubeUrl,
  parseVideoUrl,
} from "../../utils/videoUtils";

// Add a unique ID generator for consistent tracking
let factoryInstanceCounter = 0;

interface VideoPlayerFactoryProps {
  item: any;
  playbackCycle?: number;
  keepAliveEmbedded?: boolean;
  onEnded: () => void;
  onError?: (error: string) => void;
  onTimeUpdate?: (current: number, duration: number) => void;
  className?: string;
  fillMode?: "contain" | "cover";
  screenData?: {
    showPortraitPosterOnLandscape?: boolean;
    showLandscapePosterOnPortrait?: boolean;
    orientation?: "landscape" | "portrait";
  };
}

const VideoPlayerFactory: React.FC<VideoPlayerFactoryProps> = ({
  item,
  playbackCycle = 0,
  keepAliveEmbedded = false,
  onEnded,
  onError,
  className,
  fillMode = "contain",
  screenData = {},
  onTimeUpdate,
}) => {
  // Create a stable instance ID for this factory instance that persists across renders
  const instanceIdRef = useRef(`vpf-${++factoryInstanceCounter}`);
  const itemIdRef = useRef(item?.id || item?.assetId || "unknown");
  const videoTypeRef = useRef(
    item?.videoType || (isYouTubeUrl(item?.assetUrl) ? "youtube" : "direct"),
  );
  const creationTimeRef = useRef(Date.now());

  // NEW: Add a ref to track if this component is still mounted
  const isMountedRef = useRef(true);
  // NEW: Add a ref to prevent multiple onEnded calls
  const hasEndedRef = useRef(false);

  // NEW: Wrap onEnded with checks to prevent multiple calls
  const handleVideoEnded = React.useCallback(() => {
    // Only process if component is mounted and hasn't already ended
    if (!isMountedRef.current || hasEndedRef.current) {
      console.log(
        `🛑 [VideoPlayerFactory-${
          instanceIdRef.current
        }] Prevented duplicate onEnded call for ${
          item?.assetTitle || "Untitled"
        } (already processed: ${hasEndedRef.current}, mounted: ${
          isMountedRef.current
        })`,
      );
      return;
    }

    // Mark as ended to prevent duplicate calls
    hasEndedRef.current = true;

    const elapsedTime = Date.now() - creationTimeRef.current;
    console.log(
      `📊 [VideoPlayerFactory-${instanceIdRef.current}] VIDEO ENDED: ${
        item?.assetTitle || "Untitled"
      } (ID: ${itemIdRef.current}, type: ${
        videoTypeRef.current
      }) after ${Math.round(elapsedTime / 1000)}s`,
    );

    // Small delay to ensure cleanup has happened
    setTimeout(() => {
      if (isMountedRef.current) {
        onEnded();
      }
    }, 100);
  }, [item, onEnded]);

  // Enhanced error handler with mount check
  const handleError = React.useCallback(
    (error: string) => {
      if (!isMountedRef.current) return;

      console.error(
        `❌ [VideoPlayerFactory-${instanceIdRef.current}] ERROR: ${
          item?.assetTitle || "Untitled"
        } (ID: ${itemIdRef.current}, type: ${videoTypeRef.current}) - ${error}`,
      );

      if (onError) {
        onError(error);
      }
    },
    [item, onError],
  );

  // Log creation once per actual mount
  useEffect(() => {
    console.log(
      "[VideoPlayerFactory] MOUNTED",
      item.assetTitle,
      item.assetType,
    );
    // Reset ended flag on new mount
    hasEndedRef.current = false;
    isMountedRef.current = true;
    creationTimeRef.current = Date.now();

    // Update item references on every new render
    itemIdRef.current = item?.id || item?.assetId || "unknown";
    videoTypeRef.current =
      item?.videoType || (isYouTubeUrl(item?.assetUrl) ? "youtube" : "direct");

    console.log(
      `🎬 [VideoPlayerFactory-${instanceIdRef.current}] CREATING: ${
        item?.assetTitle || "Untitled"
      } (ID: ${itemIdRef.current}, type: ${videoTypeRef.current})`,
    );
    console.log(
      `📋 [VideoPlayerFactory-${instanceIdRef.current}] PROPERTIES: duration=${
        item?.duration || "unknown"
      }, autoplay=${item?.videoAutoplay !== false}, muted=${
        item?.videoMuted !== false
      }, loop=${item?.videoLoop === true}`,
    );

    // Focused diagnostic: log the chosen per-item duration when a video mounts
    const chosenDuration = item?.duration ?? 30;
    const shortUrl = (item?.assetUrl || "").toString().slice(0, 80);
    console.log(
      `[VideoPlayerFactory-${
        instanceIdRef.current
      }] DIAG: chosenDuration=${chosenDuration}s, assetId=${
        itemIdRef.current
      }, url=${shortUrl}${
        (item?.assetUrl || "").toString().length > 80 ? "..." : ""
      }`,
    );

    console.log(
      `✅ [VideoPlayerFactory-${instanceIdRef.current}] MOUNTED: ${
        item?.assetTitle || "Untitled"
      } (ID: ${itemIdRef.current}, type: ${videoTypeRef.current})`,
    );

    // Cleanup function
    return () => {
      // Mark as unmounted to prevent callbacks after unmount
      isMountedRef.current = false;

      const elapsedTime = Date.now() - creationTimeRef.current;
      console.log(
        `🗑️ [VideoPlayerFactory-${instanceIdRef.current}] UNMOUNTING: ${
          item?.assetTitle || "Untitled"
        } (ID: ${itemIdRef.current}, type: ${
          videoTypeRef.current
        }) after ${Math.round(elapsedTime / 1000)}s`,
      );
    };
  }, [item]);

  // NEW: Safety check for missing/empty items
  if (!item || !item.assetUrl) {
    console.error(
      `❌ [VideoPlayerFactory-${instanceIdRef.current}] Missing item or asset URL`,
    );
    setTimeout(() => {
      if (isMountedRef.current && !hasEndedRef.current) {
        handleVideoEnded();
      }
    }, 100);
    return null;
  }

  const parsedVideo = parseVideoUrl(item.assetUrl || "");
  const isYouTube =
    item.videoType === "youtube" || parsedVideo.platform === "youtube";
  const isHls = item.videoType === "hls" || parsedVideo.platform === "hls";
  const isEmbedded =
    item.videoType === "embedded" || parsedVideo.platform === "embedded";

  // Add state for video URL
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Update the useEffect to fetch video URL for direct videos
  useEffect(() => {
    // Fetch video URL if it's a direct video
    if (!isYouTube && !isEmbedded && item?.assetUrl) {
      (async () => {
        try {
          const url = await assetsService.getAsset(item.assetUrl, "video");
          setVideoUrl(url);
        } catch (error) {
          console.error(
            `Failed to get asset URL for ${item.assetTitle}:`,
            error,
          );
          handleError("Failed to load video URL");
        }
      })();
    } else {
      setVideoUrl(null); // Reset for YouTube or invalid items
    }

    // Cleanup function
    return () => {
      // Mark as unmounted to prevent callbacks after unmount
      isMountedRef.current = false;

      const elapsedTime = Date.now() - creationTimeRef.current;
      console.log(
        `🗑️ [VideoPlayerFactory-${instanceIdRef.current}] UNMOUNTING: ${
          item?.assetTitle || "Untitled"
        } (ID: ${itemIdRef.current}, type: ${
          videoTypeRef.current
        }) after ${Math.round(elapsedTime / 1000)}s`,
      );
    };
  }, [item]);

  if (isYouTube) {
    const videoId = getYouTubeVideoId(item.assetUrl);

    if (!videoId) {
      console.error(
        `❌ [VideoPlayerFactory-${instanceIdRef.current}] Invalid YouTube URL: ${item.assetUrl}`,
      );
      setTimeout(() => handleVideoEnded(), 100);
      return null;
    }

    return (
      <div
        className={className}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
        data-instance-id={instanceIdRef.current}
        data-video-type="youtube"
        data-video-id={videoId}
        data-asset-id={item.assetId || item.id}
      >
        <YouTubePlayer
          key={`yt-${videoId}-${item.assetId || item.id}-${playbackCycle}`}
          videoId={videoId}
          title={item.assetTitle || "YouTube Video"}
          autoplay={item.videoAutoplay !== false}
          muted={item.videoMuted !== false}
          loop={item.videoLoop === true}
          duration={item.duration ?? 30}
          onEnded={handleVideoEnded}
          onError={handleError}
          onTimeUpdate={onTimeUpdate}
          className="youtube-player"
          fillMode={fillMode}
        />
      </div>
    );
  }

  if (isEmbedded) {
    return (
      <div
        className={className}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
        data-instance-id={instanceIdRef.current}
        data-video-type="embedded"
        data-asset-id={item.assetId || item.id}
      >
        <EmbedVideoPlayer
          key={`embed-${item.assetUrl}-${item.assetId || item.id}-${playbackCycle}`}
          src={item.assetUrl}
          title={item.assetTitle || "Embedded Stream"}
          autoplay={item.videoAutoplay !== false}
          duration={item.duration ?? 120}
          enableAutoComplete={!keepAliveEmbedded}
          onEnded={handleVideoEnded}
          onError={handleError}
          className="embedded-video-player"
          fillMode={fillMode}
        />
      </div>
    );
  } else {
    // Only render DirectVideoPlayer if videoUrl is available
    if (!videoUrl) {
      return <div>Loading video...</div>; // Or a loading indicator
    }

    if (isHls) {
      return (
        <div
          className={className}
          style={{
            width: "100%",
            height: "100%",
            position: "relative",
            overflow: "hidden",
          }}
          data-instance-id={instanceIdRef.current}
          data-video-type="hls"
          data-asset-id={item.assetId || item.id}
        >
          <HlsVideoPlayer
            key={`hls-${item.assetUrl}-${item.assetId || item.id}-${playbackCycle}`}
            src={videoUrl}
            title={item.assetTitle || "HLS Stream"}
            autoplay={item.videoAutoplay !== false}
            muted={item.videoMuted !== false}
            loop={item.videoLoop === true}
            duration={item.duration ?? 30}
            onEnded={handleVideoEnded}
            onError={handleError}
            onTimeUpdate={onTimeUpdate}
            className="hls-video-player"
            fillMode={fillMode || "contain"}
          />
        </div>
      );
    }

    return (
      <div
        className={className}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
        data-instance-id={instanceIdRef.current}
        data-video-type="direct"
        data-asset-id={item.assetId || item.id}
      >
        <DirectVideoPlayer
          key={`direct-${item.assetUrl}-${item.assetId || item.id}-${playbackCycle}`}
          src={videoUrl} // Use the fetched videoUrl instead of item.assetUrl
          title={item.assetTitle || "Video"}
          autoplay={item.videoAutoplay !== false}
          muted={item.videoMuted !== false}
          loop={item.videoLoop === true}
          duration={item.duration ?? 30}
          onEnded={handleVideoEnded}
          onError={handleError}
          onTimeUpdate={onTimeUpdate}
          className="direct-video-player"
          fillMode={fillMode || "contain"}
          showPortraitPosterOnLandscape={
            screenData.showPortraitPosterOnLandscape
          }
          showLandscapePosterOnPortrait={
            screenData.showLandscapePosterOnPortrait
          }
          screenOrientation={screenData.orientation}
          factoryId={instanceIdRef.current}
        />
      </div>
    );
  }
};

// Use React.memo but with a custom comparison to prevent unnecessary re-renders
export default React.memo(
  VideoPlayerFactory,
  // NEW: Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // Only re-render if the item ID changes
    return (
      prevProps.item?.id === nextProps.item?.id &&
      prevProps.item?.assetId === nextProps.item?.assetId &&
      prevProps.item?.assetUrl === nextProps.item?.assetUrl &&
      prevProps.playbackCycle === nextProps.playbackCycle
    );
  },
);
